import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from 'react-toastify';
import { BsCheckCircleFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";

// Imports from your uploaded files
import GenericDataLayout from "../../components/custom/GenericDataLayout";
import GenericTable from "../../components/custom/GenericTable";
import { getMethodApiCall, postMethodApiCall } from "src/services/api";
import { downloadFile } from '../../utils/helpers';

const GrievanceModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-4 sm:p-6 rounded shadow-lg w-full max-w-md relative">
        <button
          className="absolute top-2 right-2 text-gray-500 text-xl"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-lg font-semibold mb-4 text-primary">
          {t("trainer.grievanceRedressalForm")}
        </h2>
        <form className="space-y-4">
          <div>
            <label className="block text-xs font-[400] text-gray-600 mb-1">
              {t("trainer.subject")}
            </label>
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder={t("trainer.enterSubject")}
            />
          </div>
          <div>
            <label className="block text-xs font-[400] text-gray-600 mb-1">
              {t("trainer.description")}
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm"
              rows={4}
              placeholder={t("trainer.describeGrievance")}
            />
          </div>
          <button
            type="submit"
            className="bg-primary hover:bg-primary/90 text-white font-semibold py-2 px-6 rounded w-full"
          >
            {t("trainer.submit")}
          </button>
        </form>
      </div>
    </div>
  );
};

const TrainerDashboard = () => {
  const { t } = useTranslation();

  // State
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
  });

  // Filters State
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(1, "day").format("YYYY-MM-DD")
  );
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal State
  const [verifyingId, setVerifyingId] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // NEW STATE: Tracks specific action ('APPROVE', 'REJECT', or null)
  const [verifyingAction, setVerifyingAction] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const user = JSON.parse(localStorage.getItem("loginedUser"));

  const fetchAttendance = async (
    page = 1,
    limit = pagination.limit,
    customStartDate = startDate,
    customEndDate = endDate,
    customStatus = statusFilter
  ) => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("loginedUser"));
      const trainerId = user?.trainer?.id || user?.id;

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (customStartDate && customEndDate) {
        params.append("startDate", customStartDate);
        params.append("endDate", customEndDate);
      }
      if (customStatus === "active") params.append("verified", "true");
      else if (customStatus === "inactive") params.append("verified", "null");
      else if (customStatus === "rejected") params.append("verified", "false");

      const res = await getMethodApiCall(
        `/v1/api/trainer/trainee-attendance/${trainerId}?${params.toString()}`);

      setAttendance(res.data.data || []);
      setPagination({
        page: res.data.pagination?.page || 1,
        limit: limit, // Ensure limit persists
        totalRecords: res.data.pagination?.total || 0,
        totalPages: res.data.pagination?.totalPages || 1,
      });
    } catch (err) {
      console.error(err);
      setAttendance([]);
      setPagination((prev) => ({ ...prev, totalRecords: 0, page: 1 }));
    } finally {
      setLoading(false);
    }
  };

const handleDownload = async () => {
  setIsDownloading(true);
  try {
    const user = JSON.parse(localStorage.getItem('loginedUser'));
    const trainerId = user?.trainer?.id || user?.id;

    const params = new URLSearchParams();
    if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
    }
    if (statusFilter === 'active') params.append('verified', 'true');
    else if (statusFilter === 'inactive') params.append('verified', 'null');
    else if (statusFilter === 'rejected') params.append('verified', 'false');

    const url = `/v1/api/trainer/trainee-attendance/${trainerId}/download?${params.toString()}`;
    const res = await getMethodApiCall(url, {}, {
      responseType: "blob",
    });

    downloadFile({
      data: res.data,
      mimeType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      filePrefix: `Trainer_Attendance_${user?.trainer?.name || 'data'}_${moment().format("YY-MM-DD_HHmmss")}`,
      extension: "xlsx",
    });
    toast.success('File downloaded');
  } catch (error) {
    console.error(error);
  } finally {
    setIsDownloading(false);
  }
};

  // Effect to trigger fetch on filter change
  useEffect(() => {
    fetchAttendance(1, pagination.limit, startDate, endDate, statusFilter);
    // eslint-disable-next-line
  }, [startDate, endDate, statusFilter]);
  // Pagination Handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchAttendance(
        newPage,
        pagination.limit,
        startDate,
        endDate,
        statusFilter
      );
    }
  };

  const handleLimitChange = (newLimit) => {
    fetchAttendance(1, newLimit, startDate, endDate, statusFilter);
  };

  const handleVerifyClick = (id) => {
    setVerifyingId(id);
    setShowVerifyModal(true);
  };

  // --- APPROVE LOGIC ---
  const handleVerifyConfirm = async () => {
    if (!verifyingId) return;

    setVerifyingAction("APPROVE"); // Set action to approve
    try {
      await postMethodApiCall(
        "/v1/api/trainer/verify-attendance",
        { attendance_id: verifyingId, status: true },
      );
      setShowVerifyModal(false);
      setVerifyingId(null);
      fetchAttendance(
        pagination.page,
        pagination.limit,
        startDate,
        endDate,
        statusFilter
      );
    } catch (err) {
      alert("Failed to approve attendance.");
    } finally {
      setVerifyingAction(null); // Reset
    }
  };

  // --- REJECT LOGIC ---
  const handleVerifyCancel = async () => {
    if (!verifyingId) return;

    setVerifyingAction("REJECT"); // Set action to reject
    try {
      await postMethodApiCall(
        "/v1/api/trainer/verify-attendance",
        { attendance_id: verifyingId, status: false },
      );
      setShowVerifyModal(false);
      setVerifyingId(null);
      fetchAttendance(
        pagination.page,
        pagination.limit,
        startDate,
        endDate,
        statusFilter
      );
    } catch (err) {
      alert("Failed to reject attendance.");
    } finally {
      setVerifyingAction(null); // Reset
    }
  };

  const handleClearFilters = () => {
    const defaultStart = moment().format("YYYY-MM-DD");
    const defaultEnd = moment().add(1, "day").format("YYYY-MM-DD");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setStatusFilter("all");
  };

  // --- Configuration for GenericDataLayout ---
  const filtersConfig = [
    {
      type: "date",
      label: t("attendance.startDate"),
      value: startDate,
      onChange: setStartDate,
      max: endDate,
    },
    {
      type: "date",
      label: t("attendance.endDate"),
      value: endDate,
      onChange: setEndDate,
      min: startDate,
    },
    {
      type: "select",
      label: t("trainer.traineeStatus"),
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        {label: t("trainer.rejected"), value: "rejected" },
        { label: t("trainer.verified"), value: "active" },
        { label: t("trainer.notVerified"), value: "inactive" },
      ],
    },
  ];

  const paginationConfig = {
    ...pagination,
    onPageChange: handlePageChange,
    onLimitChange: handleLimitChange,
  };

  const columns = [
    {
      header: t("trainer.table.date"),
      render: (row) => moment(row.date).format("DD/MM/YYYY"),
    },
    {
      header: t("trainer.table.regId"),
      render: (row) => row.trainee?.registration_id || "-",
    },
    {
      header: t("trainer.table.traineeName"),
      render: (row) => row.trainee?.name || "-",
    },
    {
      header: t("itiHome.checkIn"),
      render: (row) => moment(row?.checkin_time).format("h:mm A"),
    },
    {
      header: t("itiHome.checkOut"),
      render: (row) =>
        row?.checkout_time ? (
          moment(row?.checkout_time).format("h:mm A")
        ) : (
          <span className="text-red-500">Pending</span>
        ),
    },
    {
      header: t("trainer.table.batch"),
      render: (row) => row?.trainee?.batch_of_trade || row.batch_of_trade || "-",
    },
    {
      header: t("trainer.table.trade"),
      render: (row) => row.trade_master?.trade_name || "-",
    },
    {
      header: t("attendance.table.iti"),
      render: (row) => row.iti_master?.iti_name  || "-",
    },
    {
      header: t("attendance.table.industry"),
      render: (row) => (
        <div className="max-w-[150px] cursor-pointer truncate" title={row?.industry_master?.industry_name || ""}>
          {row?.industry_master?.industry_name || "-"}
        </div>
      )
    },
    {
      header: t("attendance.table.checkout_questions"),
      render: (row) =>
        row?.checkout_questions_filled ? (
          <span className="text-green-500 text-xs font-medium">Submitted</span>
        ) : (
          <span className="text-red-500 text-xs font-medium">Not Submitted</span>
        ),
    },
    {
      header: t("attendance.table.hours_logged"),
      render: (row) => row?.hours_logged,
    },
    {
      header: t("trainer.table.verification"),
      render: (row) => {
        const verified = row.verified;

        if (verified === true) {
          return (
            <span className="flex items-center justify-center">
              <BsCheckCircleFill className="text-green-500" size={24} title={t("trainer.verified")}/>
            </span>
          );
        }
        if (verified === false) {
          
          return (
            <span className="flex items-center justify-center">
              <button
                className="focus:outline-none"
                onClick={() => handleVerifyClick(row.id)}
                title={t("trainer.rejected")}
              >
                <BsCheckCircleFill className="text-red-500" size={24} />
              </button>
            </span>
          );
        }

        return (
          <span className="flex items-center justify-center">
            <button
              className="focus:outline-none"
              onClick={() => handleVerifyClick(row.id)}
              title={t("trainer.notVerified")}
            >
              <BsCheckCircleFill
                className="text-gray-400 hover:text-primary"
                size={24}
              />
            </button>
          </span>
        );
      },
    },
  ];
  const selectedAttendance = attendance.find(
  (item) => item.id === verifyingId
);

const isRejected = selectedAttendance?.verified === false;


  return (
    <>
      <GenericDataLayout
        subtitle={t("trainer.title")}
        title={`Welcome ${user.trainer?.name}`}
        itiName={`${user.trainer?.iti_master?.iti_name || 'N/A'} `}
        filters={filtersConfig}
        onResetFilters={handleClearFilters}
        pagination={paginationConfig}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        isLoading={loading}
        actions={[]}
      >

        <GenericTable 
          columns={columns} 
          data={attendance} 
          pagination={pagination} 
        />
      </GenericDataLayout>

      {/* --- Modals --- */}

      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded shadow-lg w-full max-w-sm relative">
            {/* Close Button */}
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl focus:outline-none"
              onClick={() => {
                if (verifyingAction === null) {
                  setShowVerifyModal(false);
                  setVerifyingId(null);
                }
              }}
              aria-label="Close"
              disabled={verifyingAction !== null}
            >
              &times;
            </button>

            <h2 className="text-lg font-semibold mb-4 text-primary text-center">
              {t("trainer.verifyAttendance")}
            </h2>


            <div className="flex flex-col sm:flex-row justify-center gap-2 mt-6">
              {/* REJECT (Cancel) BUTTON */}
              <button
                className={`px-4 py-2 rounded text-sm sm:text-base flex items-center justify-center gap-2 ${
                  verifyingAction !== null || isRejected
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
                onClick={handleVerifyCancel}
                disabled={verifyingAction !== null || isRejected === true}
              >
                {verifyingAction === "REJECT" ? (
                  <>
                    <span>Rejecting...</span>
                  </>
                ) : (
                  t("trainer.cancel")
                )}
              </button>

              {/* APPROVE (OK) BUTTON */}
              <button
                className={`px-4 py-2 rounded text-white text-sm sm:text-base flex items-center justify-center gap-2 ${
                  verifyingAction !== null
                    ? "bg-primary/50 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90"
                }`}
                onClick={handleVerifyConfirm}
                disabled={verifyingAction !== null}
              >
                {verifyingAction === "APPROVE" ? (
                  <>
                    <span>Approving...</span>
                  </>
                ) : (
                  t("trainer.ok")
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <GrievanceModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default TrainerDashboard;